import { VacancyPromptPayload } from 'utils/vacancy-generator/vacancyHelpers';
import { AdChannel, Metrics } from './adTypes';
import { CustomerResource } from './apiTypes';
import { WordPressIconItem } from './wordpressTypes';
import { Brand } from './customer/types';
import { Targeting } from './targetingTypes';

export type Vacancy = {
  key: number;
  label: string;
  title: string;
  published: boolean;
  status: VacancyStatus;
  spend_status: VacancySpendStatus;
  customer_id: number;
  brand_id: number;
  company: string;
  budget: number;
  customer_type?: string;
  created_at: string;
  has_custom_url: boolean;
  language_id: number;
  templates: number[];
  brand?: Brand;
  channels: AdChannel[];
  location?: string;
  vacCopy?: VacancyCopy;
  targeting?: Targeting;
  typeform_url?: string;
} & VacancySalaryFields;

export type PayPeriod = 'hour' | 'day' | 'month' | 'year';

export type SalaryAmountType = 'single' | 'from' | 'range';

export type SchemaBaseSalary = {
  '@type': 'MonetaryAmount';
  currency: string;
  value: {
    '@type': 'QuantitativeValue';
    unitText: 'HOUR' | 'DAY' | 'MONTH' | 'YEAR';
    value?: number;
    minValue?: number;
    maxValue?: number;
  };
};

export type SalaryDisplayMetadata = {
  language_id: number;
  locale: string;
  label: string;
  from: string;
  gross: string;
  pay_period_suffixes: Record<PayPeriod, string>;
  formatted_value?: string | null;
  formatted_line?: string | null;
};

export type SalaryMetadata = VacancySalaryFields & {
  has_salary: boolean;
  amount_type: SalaryAmountType | null;
  schema_base_salary: SchemaBaseSalary | null;
  display?: SalaryDisplayMetadata | null;
};

export type VacancySalaryFields = {
  salary_min?: number | null;
  salary_max?: number | null;
  currency?: string | null;
  pay_period?: PayPeriod | null;
  salary_note?: string | null;
  salary_metadata?: SalaryMetadata | null;
};

export enum VacancyStatus {
  DRAFT = 'DRAFT',
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum VacancySpendStatus {
  NONE = 'NONE',
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export type VacancyMetric = {
  customer_type?: string;
  status: VacancyStatus;
  spend_status: VacancySpendStatus;
  vacBudget?: VacancyBudget | null;
  vacSpent?: number | null;
  vacApplications?: number | null;
  vacMetrics?: Metrics[];
};

type VacancyBudget = {
  total_budget: number | null;
};

export interface VacancyContextType {
  copyData?: VacancyCopy;
  /** The campaign `copyData` belongs to; see VacancyContext. */
  copyVacancyId?: number | null;
  setCopyData: React.Dispatch<React.SetStateAction<VacancyCopy | undefined>>;
  saveHandler: (vacancyData?: VacancyCopy) => Promise<void>;
  saveCustomUrlHandler: (customUrl: string, cb?: () => void) => Promise<void>;
  dummyTxt: string;
  copyLoading: boolean;
  refetchCopy: () => void;
  generatingFields: (keyof VacancyCopy)[];
  setGeneratingFields: React.Dispatch<
    React.SetStateAction<(keyof VacancyCopy)[]>
  >;
  payload?: VacancyPromptPayload;
  setPayload: React.Dispatch<
    React.SetStateAction<VacancyPromptPayload | undefined>
  >;
}

export type VacancyCopyKeyname =
  | 'vac_text_uniqueness'
  | 'vac_text_essence'
  | 'procedure_text'
  | 'vac_text_todo'
  | 'vac_text_expectations'
  | 'vac_text_offer'
  | 'vac_text_company';

export type VacancyCopy = {
  /** When the server last kept this page (5b item 3). */
  kept_at?: string | null;
  /** D10: the count of the job page this content was read at. */
  kept_version?: number | null;
  id?: number;
  vac_url?: string;
  share_url?: string;
  url?: string;
  // Theming
  primary_color: string;
  secondary_color: string;
  tertiary_color: string;
  // Resources
  pictures?: {
    logo?: CustomerResource | null;
    image_cb1?: CustomerResource | null;
    image_cb3?: CustomerResource | null;
  } | null;
  // Titles
  title?: string;
  procedure_title?: string;
  todo_title?: string;
  expectations_title?: string;
  offer_title?: string;
  company_title?: string;
  page_cta?: string;
  // Lists
  vac_text_uniqueness?: WordPressIconItem[];
  /** HTML-string */
  vac_text_essence?: string;
  /** HTML-string */
  procedure_text?: string;
  /** HTML-string */
  vac_text_todo?: string;
  /** HTML-string */
  vac_text_expectations?: string;
  /** HTML-string */
  vac_text_offer?: string;
  /** HTML-string */
  vac_text_company?: string;
} & VacancySalaryFields;

export interface UpdateVacancyCopy {
  vacancyId: number;
  input: {
    id?: number;
    vac_url?: string;
    // Theming
    primary_color: string;
    secondary_color: string;
    tertiary_color: string;
    // Resources
    pictures: {
      logo: number | null;
      image_cb1: number | null;
      image_cb3: number | null;
    } | null;
    // Titles
    title?: string;
    procedure_title?: string;
    todo_title?: string;
    expectations_title?: string;
    offer_title?: string;
    company_title?: string;
    page_cta?: string;
    // Lists
    vac_text_uniqueness?: string;
    /** HTML-string */
    vac_text_essence?: string;
    /** HTML-string */
    procedure_text?: string;
    /** HTML-string */
    vac_text_todo?: string;
    /** HTML-string */
    vac_text_expectations?: string;
    /** HTML-string */
    vac_text_offer?: string;
    /** HTML-string */
    vac_text_company?: string;
  } & VacancySalaryFields;
}
